"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useSession, signOut } from "next-auth/react";
import { BRAND } from "@/lib/assets";

const navLinks = [
  { href: "/services", label: "Services" },
  { href: "/portfolio", label: "Our Work" },
  { href: "/about", label: "About Us" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();
  const { data: session } = useSession();
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Collapse the mobile menu when the visitor taps outside the nav or presses
  // Escape. The hamburger button lives inside navRef, so opening the menu
  // never trips the outside handler. Listeners are only attached while the
  // menu is open, so there is no cost on every page.
  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  // Close on navigation as a catch-all (browser back, programmatic routing),
  // in addition to the per-link onClick handlers below.
  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  return (
    <nav
      ref={navRef}
      className={`fixed top-0 left-0 right-0 z-50 bg-black transition-shadow duration-300 ${
        scrolled ? "shadow-[0_4px_30px_rgba(0,0,0,0.4)]" : ""
      }`}
    >
      {/* ── Top microbar ─────────────────────────────────────── */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-7">
          <p className="text-white/40 text-[9px] uppercase tracking-[0.3em] font-semibold hidden sm:block">
            Serving the Tri-State Since 1975
          </p>
          <div className="flex items-center gap-5 ml-auto">
            <a
              href="tel:+16063298055"
              className="text-white/50 text-[10px] uppercase tracking-[0.2em] font-semibold hover:text-white transition-colors flex items-center gap-1.5"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              (606) 329-8055
            </a>
            <Link
              href="/contact-us"
              className="text-white/50 text-[10px] uppercase tracking-[0.2em] font-semibold hover:text-white transition-colors"
            >
              Contact
            </Link>
            {session ? (
              <>
                <Link
                  href="/account"
                  className="text-white/50 text-[10px] uppercase tracking-[0.2em] font-semibold hover:text-white transition-colors"
                >
                  My Orders
                </Link>
                <button
                  onClick={() => signOut({ callbackUrl: "/" })}
                  className="text-white/50 text-[10px] uppercase tracking-[0.2em] font-semibold hover:text-white transition-colors"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <Link
                href="/account"
                className="text-white/50 text-[10px] uppercase tracking-[0.2em] font-semibold hover:text-white transition-colors"
              >
                Sign In
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* ── Main nav bar ─────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative flex items-center justify-between h-[60px]">
          {/* Logo */}
          <Link href="/" className="shrink-0 flex items-center">
            <Image
              src={BRAND.logo.primaryPng}
              alt="Area Advertising"
              width={96}
              height={48}
              // Logo displays at 44px tall (h-11). sizes="48px" makes
              // next/image request ~96px (2x for retina) instead of a
              // wasteful 256px-wide candidate for a 44px slot.
              sizes="48px"
              className="h-11 w-auto object-contain"
              priority
              fetchPriority="high"
            />
          </Link>

          {/* Desktop nav links — absolutely centered in the bar.
              lg, not md: centred absolutely, these links do not get pushed
              aside by a wider CTA group, they get painted over by it. With
              two buttons on the right there is no room for logo + links +
              both between 768 and 1023, so that range takes the hamburger. */}
          <div className="hidden lg:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
            {navLinks.map((link) => {
              const isActive = pathname === link.href;
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`text-[11px] uppercase tracking-[0.2em] font-semibold transition-colors duration-150 ${
                    isActive ? "text-brand-orange" : "text-white/80 hover:text-white"
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-stretch gap-3">
            {/* Quote — solid white, the secondary action. */}
            <Link
              href="/order"
              className="bg-white hover:bg-gray-200 text-brand-black text-[10px] uppercase tracking-[0.25em] font-bold px-5 transition-colors duration-200 flex items-center"
            >
              Get a Quote
            </Link>
            {/* Design Studio — orange, the primary call to action. */}
            <Link
              href="/designer"
              className="bg-brand-orange hover:bg-brand-orange-dark text-white text-[10px] uppercase tracking-[0.25em] font-bold px-6 py-2.5 transition-all duration-200 flex items-center gap-2 group"
            >
              Start Designing
              <svg
                className="w-3 h-3 group-hover:translate-x-0.5 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-white p-2"
            aria-label="Toggle menu"
            aria-expanded={isOpen}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {isOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* ── CMYK accent stripe ────────────────────────────────── */}
      <div className="flex h-[3px]">
        <div className="flex-1 bg-cyan-400" />
        <div className="flex-1 bg-brand-orange" />
        <div className="flex-1 bg-yellow-400" />
        <div className="flex-1 bg-black border-t border-white/10" />
      </div>

      {/* ── Mobile menu ───────────────────────────────────────── */}
      {isOpen && (
        <div className="lg:hidden bg-black border-t border-white/10 px-4 py-5 space-y-1">
          {navLinks.map((link) => {
            const isActive = pathname === link.href;
            return (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setIsOpen(false)}
                className={`flex items-center gap-3 px-2 py-3 text-[11px] uppercase tracking-[0.25em] font-semibold border-b border-white/5 transition-colors last:border-b-0 ${
                  isActive ? "text-brand-orange" : "text-white/70 hover:text-white"
                }`}
              >
                {isActive && (
                  <span className="w-1 h-1 bg-brand-orange rounded-full shrink-0" />
                )}
                {link.label}
              </Link>
            );
          })}

          {/* Mobile CTA */}
          <Link
            href="/order"
            onClick={() => setIsOpen(false)}
            className="flex items-center justify-center gap-2 mt-4 bg-white text-brand-black text-[11px] uppercase tracking-[0.2em] font-bold px-6 py-3 hover:bg-gray-200 transition-colors"
          >
            Get a Quote
          </Link>
          <Link
            href="/designer"
            onClick={() => setIsOpen(false)}
            className="flex items-center justify-center gap-2 mt-2 bg-brand-orange text-white text-[11px] uppercase tracking-[0.2em] font-bold px-6 py-3 hover:bg-brand-orange-dark transition-colors"
          >
            Start Designing
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>

        </div>
      )}
    </nav>
  );
}
